import { useEffect, useId, useState } from 'react';
import type { ShopSettings } from '@cracked-mug/shared';
import { Button } from '../../components/ui/Button';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';
import { fetchSettings, updateSettings } from '../../services/settingsApi';

export function AdminSettings() {
  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = <K extends keyof ShopSettings>(key: K, value: ShopSettings[K]): void => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async (): Promise<void> => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await updateSettings({
        isOpen: settings.isOpen,
        taxRate: settings.taxRate,
        printerIp: settings.printerIp,
        printerPort: settings.printerPort,
        estimatedPrepMinutes: settings.estimatedPrepMinutes,
      });
      setSettings(saved);
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="p-6">
        {error !== null ? (
          <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
        ) : (
          <p className="font-mono text-sm text-wca-gray-mid">Loading settings…</p>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-2xl">
      <h1 className="font-display text-3xl">Settings</h1>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
      )}

      <section className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card p-5 space-y-4">
        <h2 className="font-display text-xl">Shop status</h2>
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={settings.isOpen}
            onChange={(e) => update('isOpen', e.target.checked)}
            className="peer sr-only"
          />
          <span className="relative h-6 w-11 shrink-0 rounded-full bg-wca-gray-light transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:bg-green-600 peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-wca-red peer-focus-visible:ring-offset-2" />
          <span className="text-sm font-medium">Accepting orders</span>
        </label>
        <p className="text-xs text-wca-gray-mid">
          When this is off, teachers see the menu but can&rsquo;t place orders. Both admins and
          baristas can also flip this toggle from the Kitchen page.
        </p>
        <Field label="Estimated prep time (minutes)">
          {(id) => (
            <input
              id={id}
              type="number"
              min={1}
              max={120}
              value={settings.estimatedPrepMinutes}
              onChange={(e) => update('estimatedPrepMinutes', Number(e.target.value))}
              className={cn(fieldClass, 'w-32 font-mono')}
            />
          )}
        </Field>
      </section>

      <section className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card p-5 space-y-4">
        <h2 className="font-display text-xl">Pricing</h2>
        <Field label="Tax rate (decimal)" hint="e.g. 0.0725 = 7.25%">
          {(id) => (
            <input
              id={id}
              type="number"
              min={0}
              max={1}
              step={0.0001}
              value={settings.taxRate}
              onChange={(e) => update('taxRate', Number(e.target.value))}
              className={cn(fieldClass, 'w-32 font-mono')}
            />
          )}
        </Field>
      </section>

      <section className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card p-5 space-y-4">
        <h2 className="font-display text-xl">Receipt printer</h2>
        <Field label="IP address">
          {(id) => (
            <input
              id={id}
              type="text"
              value={settings.printerIp}
              onChange={(e) => update('printerIp', e.target.value)}
              placeholder="192.168.1.100"
              className={cn(fieldClass, 'font-mono')}
            />
          )}
        </Field>
        <Field label="Port">
          {(id) => (
            <input
              id={id}
              type="number"
              value={settings.printerPort}
              onChange={(e) => update('printerPort', Number(e.target.value))}
              className={cn(fieldClass, 'w-32 font-mono')}
            />
          )}
        </Field>
        <p className="text-xs text-wca-gray-mid">
          Use the API endpoint <code className="font-mono">POST /api/v1/printer/test</code> after
          saving to verify the printer is reachable.
        </p>
      </section>

      <div className="flex items-center gap-4">
        <Button onClick={() => void handleSave()} disabled={saving} variant="primary" size="lg">
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        {savedAt && (
          <span className="text-xs text-wca-gray-mid">
            Saved {savedAt.toLocaleTimeString('en-US')}
          </span>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (id: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {children(id)}
      {hint !== undefined && <p className="text-xs text-wca-gray-mid">{hint}</p>}
    </div>
  );
}
